import React, { useEffect, useState } from 'react';
import { CheckCircle, Coins, ArrowRight, Loader } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [processing, setProcessing] = useState(true);
  const [creditsAdded, setCreditsAdded] = useState(false);

  useEffect(() => {
    // Poll for payment status and process credits
    if (sessionId) {
      const checkStatus = async () => {
        try {
          const response = await axios.get(`${API}/credits/purchase/status/${sessionId}`);
          
          if (response.data.processed) {
            setCreditsAdded(true);
            setProcessing(false);
            console.log('Credits added successfully!');
            
            // Dispatch event to update credit balance in navigation
            window.dispatchEvent(new Event('creditBalanceUpdated'));
          } else {
            // Keep polling if not processed yet
            setTimeout(checkStatus, 2000);
          }
        } catch (error) {
          console.error('Error checking credit purchase status:', error);
          // Stop processing after error but show success page anyway
          setProcessing(false);
        }
      };
      
      checkStatus();
    } else {
      setProcessing(false);
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          {/* Success Icon or Loading */}
          {processing ? (
            <div className="mx-auto w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mb-6">
              <Loader className="w-12 h-12 text-white animate-spin" />
            </div>
          ) : (
            <div className="mx-auto w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
          )}

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-white mb-4">
            {processing ? 'Processing Your Purchase...' : 'Credits Purchased Successfully! 🎉'}
          </h1>
          
          <p className="text-white/80 mb-8">
            {processing 
              ? 'Please wait while we add credits to your account...'
              : 'Your credits have been added to your account. You can now unlock premium features and enhance your dating experience!'
            }
          </p>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={() => navigate('/credits')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center space-x-2"
            >
              <Coins className="w-5 h-5" />
              <span>View Credit Balance</span>
            </button>

            <button
              onClick={() => navigate('/credits?tab=services')}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center space-x-2"
            >
              <span>Explore Premium Features</span>
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-white/10 hover:bg-white/20 text-white py-3 px-6 rounded-xl transition-colors"
            >
              Return to Home
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-white/10 rounded-xl">
            <p className="text-white/70 text-sm">
              🔔 You should receive a confirmation notification shortly. 
              Check your notifications for details about your purchase.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditSuccess;